/**
 * ClipBasketModal — LEGACY_DECOMMISSIONED REDIRECT SHIM (P0 May 24, 2026).
 *
 * Quarantine status: LEGACY_DECOMMISSIONED — inventoried in
 *   /app/scripts/legacy_upload.yaml under `frontend_legacy_files`.
 *   See /app/memory/LEGACY_UPLOAD_DEPENDENCY_MAP.md for the full
 *   classification and /app/memory/MEDIA_PATH_TRUTH_AUDIT_V1.md
 *   for the canonical V3 path declaration that supersedes V1/V2.
 *
 * Operator directive (May 24, 2026):
 *   "ClipBasketModal V1 — Best option: remove/sunset it or redirect
 *    it to ClipBasketV2. Do not maintain two competing multi-clip
 *    upload systems."
 *
 * What this file used to be: a 2,422-line legacy multi-clip uploader
 * that routed every clip through the chunked local relay
 * (`POST /api/uploads/chunked/init` with purpose="clip_basket"). On
 * May 24, 2026 the platform pivoted to a server-enforced R2-native
 * doctrine that returns HTTP 410 for chunked uploads of
 * clip_basket payloads > 25 MB. Every meaningful iPhone clip is
 * larger than that, so V1's upload step has been refused at the
 * server boundary since that date.
 *
 * What this file IS now: a thin redirect shim that mounts the
 * canonical ClipBasketV2 component while preserving the exact
 * V1 prop signature. All five legacy call sites continue to work
 * without code changes:
 *
 *   • InstructorPendingRequestsCard.jsx
 *   • InstructorTrainingUploadModal.jsx
 *   • TrainingMode.jsx
 *   • LessonReview.js
 *   • EndLessonReviewNudge.jsx (via the V1 → V2 surface)
 *
 * Behavioural deltas (intentional, surfaced to console for the
 * operator's visibility):
 *
 *   • V2 does not yet honour `requestId`, `pupilName`, `focusArea`,
 *     `focusOther`, `timeRange`, `selfReview`, `proofContext`,
 *     `reviewIntent`, or `resumeBasketId`. They are forwarded as
 *     a console.warn so the operator can see exactly which surface
 *     lost which V1-only context. The upload itself proceeds — the
 *     R2 path is the same regardless of these fields, and they do
 *     not affect bytes-on-the-wire or disk safety. Downstream
 *     surfaces that consume those fields will continue to work via
 *     the basket lookup performed after `onHandedOff` fires.
 *
 *   • `intent` → `initialIntent` is the only direct prop mapping.
 *
 * Why a shim instead of deleting the file:
 *   • Five callers still import this exact module path.
 *   • Operator's brief says "redirect safely" is acceptable, and
 *     ripping out 5 imports + JSX blocks is higher-risk than the
 *     2-line shim — especially since V2 covers the only path that
 *     mattered for scale safety (the upload).
 */
import { useEffect } from "react";
import ClipBasketV2 from "../upload/ClipBasketV2";
import { useAuth } from "../../context/AuthContext";

export default function ClipBasketModal(props) {
  const {
    // Forwarded to V2.
    onClose,
    onHandedOff,
    intent = null,
    // V1-only context. Logged for operator visibility but not
    // wired into V2 yet. See the file header for the rationale.
    requestId = null,
    pupilName = null,
    focusArea = null,
    focusOther = null,
    timeRange = null,
    selfReview = false,
    proofContext = null,
    reviewIntent = null,
    resumeBasketId = null,
    beaconSource = "clip_basket",
  } = props || {};

  const { user } = useAuth();

  useEffect(() => {
    const v1Only = {
      requestId, pupilName, focusArea, focusOther, timeRange,
      selfReview, proofContext, reviewIntent, resumeBasketId,
      beaconSource,
    };
    const provided = Object.entries(v1Only).filter(
      ([, v]) => v !== null && v !== false && v !== "clip_basket",
    );
    if (provided.length > 0 && typeof console !== "undefined" && console.warn) {
      // eslint-disable-next-line no-console
      console.warn(
        "[ClipBasketModal] redirected to ClipBasketV2. The following "
          + "V1-only props were not forwarded (V2 doctrine ignores them "
          + "for now; downstream surfaces still resolve them via the "
          + "basket lookup after onHandedOff):",
        Object.fromEntries(provided),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ClipBasketV2
      user={user}
      onClose={onClose}
      onHandedOff={onHandedOff}
      initialIntent={intent}
    />
  );
}
